<template>
  <footer class="bg-gray-900 text-white border-t border-gray-800 pt-4 pb-12">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Contact Information -->
        <div>
          <h3 class="text-base sm:text-lg font-semibold mb-4">Contact Us</h3>
          <div class="space-y-3 text-gray-300">
            <p v-if="contactPhone" class="flex items-center text-base">
              <svg class="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              <a :href="`tel:${contactPhone}`" class="text-gray-300 hover:text-white transition-colors no-underline">{{ contactPhone }}</a>
            </p>
            <p v-if="contactEmail" class="flex items-center text-base">
              <svg class="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              <a :href="`mailto:${contactEmail}`" class="text-gray-300 hover:text-white transition-colors no-underline">{{ contactEmail }}</a>
            </p>
            <p v-if="contactAddress" class="flex items-start text-base">
              <svg class="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span>{{ contactAddress }}</span>
            </p>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-base sm:text-lg font-semibold mb-4">Quick Links</h3>
          <div class="space-y-1">
            <router-link to="/" class="block text-base text-gray-300 hover:text-white transition-colors py-2 rounded-lg hover:bg-gray-800 px-2 -mx-2 min-h-[44px] flex items-center">
              Home
            </router-link>
            <router-link to="/shop" class="block text-base text-gray-300 hover:text-white transition-colors py-2 rounded-lg hover:bg-gray-800 px-2 -mx-2 min-h-[44px] flex items-center">
              Shop
            </router-link>
          </div>
        </div>

        <!-- Business Hours or Additional Info -->
        <div>
          <h3 class="text-base sm:text-lg font-semibold mb-4">Information</h3>
          <div class="text-gray-300 text-xs sm:text-sm" v-html="footerInfo"></div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="border-t border-gray-700 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div class="text-gray-300 text-xs sm:text-sm font-medium">
          © {{ currentYear }} {{ brandName }}. All rights reserved.
        </div>
        <div class="flex flex-col md:flex-row items-center gap-2 md:gap-4">
          <!-- Detailed version tag for debugging -->
          <span class="text-gray-500 text-[10px] font-mono" :title="fullVersionInfo">
            v{{ appVersion }} · {{ buildTime }} · {{ commitHash }}
          </span>
          <div class="flex items-center gap-2">
            <span class="text-gray-300 text-xs sm:text-sm">Powered by</span>
            <span class="text-gray-200 text-xs sm:text-sm font-semibold">SchoolVision</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import DOMPurify from 'dompurify'

export default {
  name: 'AppFooter',
  computed: {
    contactPhone() {
      return window.LOCATION_PHONE || null
    },
    contactEmail() {
      return window.LOCATION_EMAIL || null
    },
    contactAddress() {
      return window.LOCATION_ADDRESS || null
    },
    brandName() {
      return window.LOCATION_NAME || 'E-Commerce Store'
    },
    footerInfo() {
      // Configurable footer information - can be set via window.FOOTER_INFO
      // Supports HTML including <br> tags for line breaks
      // Sanitized to prevent XSS attacks
      const rawContent = window.FOOTER_INFO || 'Secure online payments<br>Guest checkout available<br>Multiple payment methods accepted'
      return DOMPurify.sanitize(rawContent)
    },
    currentYear() {
      return new Date().getFullYear()
    },
    buildTime() {
      // Build timestamp injected at build time via vue.config.js
      const timestamp = process.env.VUE_APP_BUILD_TIME
      if (timestamp) {
        const date = new Date(timestamp)
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      }
      return 'dev'
    },
    appVersion() {
      return process.env.VUE_APP_VERSION || '0.1.0'
    },
    commitHash() {
      return process.env.VUE_APP_COMMIT_HASH || 'local'
    },
    fullVersionInfo() {
      return `Version: ${this.appVersion}\nBuild: ${this.buildTime}\nCommit: ${this.commitHash}\nBuilt: ${process.env.VUE_APP_BUILD_TIME || 'development'}`
    }
  }
}
</script>

<style scoped>
/* Reset link styles in footer to match text color */
footer a {
  color: inherit;
  text-decoration: none;
}

footer a:visited {
  color: inherit;
}

footer a:hover {
  color: #ffffff;
}

/* Ensure footer info content has proper line height */
.text-gray-300 :deep(br) {
  display: block;
  margin-bottom: 0.5rem;
  content: '';
}
</style>
